import L from 'leaflet';
import UTIF from 'utif';
import { useFleetStore } from '@/stores/fleet';
import { eventBus } from '@/main';


export class AIOLayer {
    constructor(map, layerControl) {
        this.map = map;
        this.layerControl = layerControl;
        this.overlays = {};
        this.aioPopupContainer = null;
        this.aioPopupContent = null;
        
        // Карта для отслеживания полигонов по ID объекта AIO
        this.aioElementsMap = new Map();
        // Текущий выделенный объект
        this.currentHighlightedId = null;
        
        // Закрыть popup при переключении на другой таб
        eventBus.$on('close-aio-popup', () => {
            this.hideAIOPopup();
        });
        
        // Обработчики событий выделения
        eventBus.$on('highlight-aio-on-map', this.highlightAIOElements.bind(this));
        eventBus.$on('clear-aio-highlight', this.clearHighlight.bind(this));
    }

    // Добавляем AIO на карту
    addAIOLayer() {
        const fleetStore = useFleetStore();

        if (!this.map) return;

        try {
            // Очищаем выделение и карту элементов
            this.clearHighlight();
            this.aioElementsMap.clear();

            // Создаем контейнер для всплывающих окон AIO
            this.createAIOPopupContainer();

            // Создаём группы слоёв для разных типов AIO
            const tempInfosGroup = L.layerGroup([], {pane: 'aio'});
            const permanentInfosGroup = L.layerGroup([], {pane: 'aio'});

            // Обрабатываем данные AIO
            let tempInfos = fleetStore.data.aio.tempInfos || [];
            let permanentInfos = fleetStore.data.aio.permanentInfos || [];

            // Обрабатываем данные TempInfos
            if (tempInfos.length > 0) {
                tempInfos.forEach(item => {
                    this.processAIOItem(item, tempInfosGroup, 'TempInfos', '#f5a742');
                });
            }

            // Обрабатываем данные PermanentInfos
            if (permanentInfos.length > 0) {
                permanentInfos.forEach(item => {
                    this.processAIOItem(item, permanentInfosGroup, 'PermanentInfos', '#4287f5');
                });
            }

            tempInfos = null
            permanentInfos = null

            // Объединяем все группы в один слой
            this.overlays.AIO = L.layerGroup([permanentInfosGroup, tempInfosGroup], {pane: 'aio'});

            // Добавляем слой в контрол слоёв
            if (this.layerControl) {
                this.layerControl.addOverlay(this.overlays.AIO, 'AIO');
            }

            return this.overlays.AIO;
        } catch (error) {
            // Error displaying AIO data
        }
    }

    // Создаем контейнер для всплывающих окон AIO
    createAIOPopupContainer() {
        const mapContainer = this.map.getContainer();
        this.aioPopupContainer = L.DomUtil.create('div', 'aio-popup');
        this.aioPopupContainer.style.display = 'none';

        // Запрет масштабирования и взаимодействия с картой при наведении на всплывающую подсказку
        this.aioPopupContainer.addEventListener('mouseenter', this.disableMapHandlers.bind(this));
        this.aioPopupContainer.addEventListener('mouseleave', this.enableMapHandlers.bind(this));
        this.aioPopupContainer.addEventListener('wheel', e => e.stopPropagation());

        // Кнопка закрытия
        const closeButton = L.DomUtil.create('div', 'aio-popup-close', this.aioPopupContainer);
        closeButton.innerHTML = '✕';
        closeButton.addEventListener('click', () => this.hideAIOPopup());

        // Содержимое всплывающего окна
        this.aioPopupContent = L.DomUtil.create('div', 'aio-popup-content', this.aioPopupContainer);

        mapContainer.appendChild(this.aioPopupContainer);
    }    
    
    // Обработка элемента AIO и создание полигона
    processAIOItem(item, layerGroup, category, color) {
        // Извлекаем координаты из строки Geometry
        const coordinates = this.parsePolygonGeometry(item.Geometry);
        if (!coordinates || coordinates.length < 3) {
            return;
        }

        // Создаем полигон с более заметным стилем
        const polygon = L.polygon(coordinates, {
            color: color,
            weight: 2,
            opacity: 0.8,
            fillColor: color,
            fillOpacity: 0.3,
            pane: 'aio'
        });

        // Сохраняем оригинальный стиль для восстановления
        polygon._originalStyle = {
            color: color,
            weight: 2,
            opacity: 0.8,
            fillColor: color,
            fillOpacity: 0.3
        };

        // Добавляем ID объекта к полигону
        polygon._aioId = item.Id;

        // Отслеживаем полигон по ID объекта
        if (!this.aioElementsMap.has(item.Id)) {
            this.aioElementsMap.set(item.Id, []);
        }
        this.aioElementsMap.get(item.Id).push(polygon);

        // Добавляем всплывающую подсказку при наведении
        polygon.bindTooltip(item.Label, {
            sticky: true,
            direction: 'top'
        });

        // Добавляем обработчик клика для отображения детальной информации
        polygon.on('click', async (e) => {
            // Останавливаем распространение события клика, чтобы карта не реагировала
            L.DomEvent.stopPropagation(e);
            
            // Очищаем предыдущее выделение
            this.clearHighlight();
            
            // Выделяем все элементы этого объекта AIO
            this.highlightAIOElements(item.Id);
            
            // Эмитим событие для выделения объекта в таблице
            eventBus.$emit('aio-item-selected-on-map', {
                id: item.Id,
                label: item.Label,
                info: item.Info,
                type: category === 'TempInfos' ? 'Temporary' : 'Permanent',
                data: item
            });
            
            // Эмитим дополнительное событие для выделения в таблице
            eventBus.$emit('highlight-aio-item-in-table', {
                id: item.Id,
                label: item.Label,
                info: item.Info,
                type: category === 'TempInfos' ? 'Temporary' : 'Permanent'
            });
            
            // Показываем всплывающее окно с информацией об элементе AIO
            await this.showAIOPopup(item, category);
        });

        // Добавляем полигон в соответствующую группу
        layerGroup.addLayer(polygon);
    }

    // Парсинг строки геометрии полигона
    parsePolygonGeometry(geometryString) {
        try {
            // Проверяем входящие данные
            if (!geometryString || typeof geometryString !== 'string') {
                console.warn('Invalid geometry string:', geometryString);
                return null;
            }

            // Извлекаем координаты из строки POLYGON ((x1 y1, x2 y2, ...))
            const coordsMatch = geometryString.match(/POLYGON\s*\(\((.*)\)\)/);
            if (!coordsMatch || !coordsMatch[1]) {
                console.warn('Failed to match polygon format:', geometryString);
                return null;
            }

            // Разбиваем строку координат на пары
            const coordPairs = coordsMatch[1].split(',').map(pair => pair.trim());

            // Преобразуем каждую пару в массив [lat, lon]
            const coordinates = [];

            for (const pair of coordPairs) {
                const parts = pair.split(' ').filter(part => part.trim() !== '');

                if (parts.length < 2) {
                    console.warn('Invalid coordinate pair:', pair);
                    continue;
                }

                // В формате Geometry координаты идут как (lon lat),
                // а для Leaflet нужны [lat, lon]
                const lon = parseFloat(parts[0]);
                const lat = parseFloat(parts[1]);

                // Проверяем валидность координат
                if (isNaN(lat) || isNaN(lon) || !isFinite(lat) || !isFinite(lon)) {
                    console.warn('Invalid coordinate values:', lat, lon, 'from', pair);
                    continue;
                }

                coordinates.push([lat, lon]); // Leaflet использует [lat, lon]
            }

            // Проверяем, что у нас достаточно точек для полигона
            if (coordinates.length < 3) {
                console.warn('Not enough valid coordinates for polygon:', coordinates.length);
                return null;
            }

            return coordinates;
        } catch (error) {
            console.error('Error parsing polygon geometry:', error, geometryString);
            return null;
        }
    }

    // Отображаем всплывающее окно с информацией об элементе AIO
    async showAIOPopup(item, category) {
        // Расшифровка category для отображения
        let categoryLabel;
        switch(category) {
            case 'NoInfos':
                categoryLabel = 'No Information';
                break;
            case 'TempInfos':
                categoryLabel = 'Temporary Notice';
                break;
            case 'PermanentInfos':
                categoryLabel = 'Preliminary Notice';
                break;
            default:
                categoryLabel = category;
        }

        // Определяем базовый URL для ресурсов AIO, учитывая локальное окружение
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const baseAioUrl = isLocalhost
            ? '/aio/' // Используем локальный путь для разработки
            : 'https://dev-getenc.bgeo.fi/aio/'; // Удаленный сервер для продакшн

        // Создаем HTML-содержимое
        let contentHTML = `
        <h3>${item.Label}</h3>
        <p class="aio-category">${categoryLabel}</p>
        <p>${item.Info}</p>
    `;

        // Загружаем дополнительный текст, если он указан
        if (item.Text) {
            try {
                const textUrl = `${baseAioUrl}${item.Text}`;
                console.log(`Loading AIO text from: ${textUrl}`);

                const response = await fetch(textUrl, {
                    // В локальном режиме добавляем настройки CORS
                    ...(isLocalhost && {
                        mode: 'cors',
                        credentials: 'same-origin',
                        headers: {
                            'Cache-Control': 'no-cache'
                        }
                    })
                });

                if (response.ok) {
                    // Перекодировка из Windows-1252 в UTF-8
                    const buffer = await response.arrayBuffer();
                    let textContent = new TextDecoder('windows-1252').decode(buffer);
                    contentHTML += `<div class="aio-text">${textContent}</div>`;
                } else {
                    console.warn(`Failed to load AIO text file: ${item.Text}. Status: ${response.status}`);
                    contentHTML += `<div class="aio-text-error">Failed to load additional text information</div>`;
                }
            } catch (error) {
                console.warn(`Failed to load AIO text file: ${item.Text}`, error);
                contentHTML += `<div class="aio-text-error">Error loading text: ${error.message}</div>`;
            }
        }

        // Очищаем предыдущее содержимое окна
        this.aioPopupContent.innerHTML = '';

        // Создаем div для основного контента
        const contentDiv = document.createElement('div');
        contentDiv.className = 'aio-popup-main-content';
        contentDiv.innerHTML = contentHTML;
        this.aioPopupContent.appendChild(contentDiv);

        // Если есть изображение, загружаем его и добавляем в контент
        if (item.Picture) {
            try {
                const imageUrl = `${baseAioUrl}${item.Picture}`;
                console.log(`Loading AIO image from: ${imageUrl}`);

                const imageContainer = document.createElement('div');
                imageContainer.className = 'aio-image-container';

                // Создаем контейнер для изображения с элементами управления
                const imageWrapper = document.createElement('div');
                imageWrapper.className = 'aio-image-wrapper';
                imageContainer.appendChild(imageWrapper);

                // Добавляем кнопку увеличения
                const zoomButton = document.createElement('button');
                zoomButton.className = 'aio-image-zoom-btn';
                zoomButton.innerHTML = '🔍';
                zoomButton.title = 'Увеличить изображение';
                imageWrapper.appendChild(zoomButton);

                // Проверяем, является ли файл TIFF-изображением
                const isTiff = item.Picture.toLowerCase().endsWith('.tif') || item.Picture.toLowerCase().endsWith('.tiff');

                if (isTiff) {
                    // Для TIFF файлов используем UTIF (utif.js)
                    console.log(`Loading TIFF image: ${imageUrl}`);

                    // Создаем элемент canvas для отрисовки TIFF
                    const canvas = document.createElement('canvas');
                    canvas.className = 'aio-image';
                    imageWrapper.appendChild(canvas);
                    this.aioPopupContent.appendChild(imageContainer);

                    // Загружаем TIFF-файл и отображаем на canvas
                    const response = await fetch(imageUrl, {
                        // В локальном режиме добавляем настройки CORS
                        ...(isLocalhost && {
                            mode: 'cors',
                            credentials: 'same-origin',
                            headers: {
                                'Cache-Control': 'no-cache'
                            }
                        })
                    });

                    if (!response.ok) {
                        throw new Error(`Failed to load image: ${response.status} ${response.statusText}`);
                    }

                    const arrayBuffer = await response.arrayBuffer();

                    // Используем UTIF для отрисовки
                    const ifds = UTIF.decode(arrayBuffer);
                    UTIF.decodeImage(arrayBuffer, ifds[0]);
                    const rgba = UTIF.toRGBA8(ifds[0]);

                    const width = ifds[0].width;
                    const height = ifds[0].height;

                    // Установим размеры canvas, сохраняя соотношение сторон,
                    // но ограничивая максимальную ширину и высоту
                    const maxWidth = 500;
                    const maxHeight = 400;

                    let canvasWidth = width;
                    let canvasHeight = height;

                    if (width > maxWidth) {
                        canvasWidth = maxWidth;
                        canvasHeight = (height * maxWidth) / width;
                    }

                    if (canvasHeight > maxHeight) {
                        canvasHeight = maxHeight;
                        canvasWidth = (width * maxHeight) / height;
                    }

                    canvas.width = canvasWidth;
                    canvas.height = canvasHeight;

                    const ctx = canvas.getContext('2d');

                    // Создаем ImageData из массива RGBA
                    const imgData = new ImageData(new Uint8ClampedArray(rgba.buffer), width, height);

                    // Создаем временный canvas для изображения в оригинальном размере
                    const tempCanvas = document.createElement('canvas');
                    tempCanvas.width = width;
                    tempCanvas.height = height;
                    const tempCtx = tempCanvas.getContext('2d');
                    tempCtx.putImageData(imgData, 0, 0);

                    // Рисуем масштабированное изображение на основном canvas
                    ctx.drawImage(tempCanvas, 0, 0, canvasWidth, canvasHeight);

                    console.log(`TIFF image rendered successfully: ${width}x${height}`);

                    // Сохраняем оригинальные размеры и данные изображения для зума
                    canvas.dataset.originalWidth = width;
                    canvas.dataset.originalHeight = height;

                    // Добавляем обработчик клика для увеличения изображения
                    canvas.addEventListener('click', () => this.showFullSizeImage(tempCanvas, width, height, item.Picture));
                    zoomButton.addEventListener('click', () => this.showFullSizeImage(tempCanvas, width, height, item.Picture));
                } else {
                    // Для обычных изображений используем стандартный тег img
                    const img = document.createElement('img');
                    img.src = imageUrl;
                    img.alt = 'AIO Image';
                    img.className = 'aio-image';

                    // Добавляем обработчик ошибок для изображения
                    img.onerror = (error) => {
                        console.error(`Failed to load image: ${imageUrl}`, error);
                        img.alt = 'Error loading image';
                        img.style.display = 'none';

                        const errorMsg = document.createElement('div');
                        errorMsg.className = 'aio-image-error';
                        errorMsg.innerText = 'Error loading image';
                        imageContainer.appendChild(errorMsg);
                    };

                    // Добавляем обработчик загрузки для получения настоящих размеров изображения
                    img.onload = () => {
                        // Сохраняем оригинальные размеры для зума
                        img.dataset.originalWidth = img.naturalWidth;
                        img.dataset.originalHeight = img.naturalHeight;
                    };

                    // Добавляем обработчик клика для увеличения изображения
                    img.addEventListener('click', () => this.showFullSizeImage(img, img.naturalWidth, img.naturalHeight, item.Picture));
                    zoomButton.addEventListener('click', () => this.showFullSizeImage(img, img.naturalWidth, img.naturalHeight, item.Picture));

                    imageWrapper.appendChild(img);
                    this.aioPopupContent.appendChild(imageContainer);
                }
            } catch (error) {
                console.error(`Error displaying image: ${item.Picture}`, error);
                // Добавляем сообщение об ошибке загрузки
                const errorDiv = document.createElement('div');
                errorDiv.className = 'aio-image-error';
                errorDiv.innerHTML = `<p>Error loading image: ${error.message}</p>`;
                this.aioPopupContent.appendChild(errorDiv);
            }
        }

        // Удаляем существующую кнопку закрытия, если она есть
        const existingCloseButton = this.aioPopupContainer.querySelector('.aio-popup-close');
        if (existingCloseButton) {
            existingCloseButton.remove();
        }

        // Создаем новую кнопку закрытия
        const closeButton = document.createElement('div');
        closeButton.className = 'aio-popup-close';
        closeButton.innerHTML = '✕';
        closeButton.addEventListener('click', () => this.hideAIOPopup());
        this.aioPopupContainer.appendChild(closeButton);

        // Для видимости всплывающего окна
        this.aioPopupContainer.style.display = 'block';
        this.aioPopupContainer.style.zIndex = '1000';
    }    
    
    // Скрываем всплывающее окно AIO
    hideAIOPopup() {
        if (this.aioPopupContainer) {
            this.aioPopupContainer.style.display = 'none';
        }
    }

    // Выделяем все элементы AIO объекта оранжевым цветом
    highlightAIOElements(aioId) {
        // Очищаем предыдущее выделение
        this.clearHighlight();
        
        const elements = this.aioElementsMap.get(aioId);
        if (elements && elements.length > 0) {
            this.currentHighlightedId = aioId;
            
            elements.forEach(element => {
                if (element && element.setStyle) {
                    element.setStyle({
                        color: '#ff8800',
                        weight: 4,
                        opacity: 1.0,
                        fillColor: '#ff8800',
                        fillOpacity: 0.5
                    });
                }
            });
        }
    }

    // Очищаем выделение, восстанавливая оригинальные стили
    clearHighlight() {
        if (this.currentHighlightedId) {
            const elements = this.aioElementsMap.get(this.currentHighlightedId);
            if (elements && elements.length > 0) {
                elements.forEach(element => {
                    if (element && element.setStyle && element._originalStyle) {
                        element.setStyle(element._originalStyle);
                    }
                });
            }
            this.currentHighlightedId = null;
        }
    }

    // Показать изображение в полном размере в модальном окне
    showFullSizeImage(imageElement, originalWidth, originalHeight, imageName) {
        console.log(`Showing full size image: ${imageName}, ${originalWidth}x${originalHeight}`);

        // Проверяем, существует ли уже модальное окно, если да - удаляем его
        const existingModal = document.querySelector('.aio-image-modal');
        if (existingModal) {
            existingModal.remove();
        }

        // Создаем модальное окно
        const modal = document.createElement('div');
        modal.className = 'aio-image-modal';

        // Создаем контейнер для содержимого модального окна
        const modalContent = document.createElement('div');
        modalContent.className = 'aio-image-modal-content';

        // Создаем заголовок с названием изображения
        const modalHeader = document.createElement('div');
        modalHeader.className = 'aio-image-modal-header';
        modalHeader.innerHTML = `
            <span>${imageName}</span>
            <span class="aio-image-modal-close">✕</span>
        `;
        modalContent.appendChild(modalHeader);

        // Создаем контейнер для изображения, чтобы обеспечить прокрутку
        const imageViewport = document.createElement('div');
        imageViewport.className = 'aio-image-modal-viewport';

        // Клонируем изображение в полном размере или создаем новый canvas
        if (imageElement instanceof HTMLCanvasElement) {
            // Если это TIFF (canvas), создаем новый canvas с оригинальным размером
            const fullSizeCanvas = document.createElement('canvas');
            fullSizeCanvas.width = originalWidth;
            fullSizeCanvas.height = originalHeight;
            fullSizeCanvas.className = 'aio-image-modal-img';

            // Копируем содержимое оригинального canvas
            const ctx = fullSizeCanvas.getContext('2d');
            ctx.drawImage(imageElement, 0, 0, originalWidth, originalHeight);

            imageViewport.appendChild(fullSizeCanvas);
        } else {
            // Для обычных изображений
            const fullSizeImg = document.createElement('img');
            fullSizeImg.src = imageElement.src;
            fullSizeImg.className = 'aio-image-modal-img';
            fullSizeImg.alt = imageName;

            imageViewport.appendChild(fullSizeImg);
        }

        modalContent.appendChild(imageViewport);

        // Добавляем информацию о размере изображения
        const modalFooter = document.createElement('div');
        modalFooter.className = 'aio-image-modal-footer';
        modalFooter.innerHTML = `Размер: ${originalWidth} × ${originalHeight} пикселей`;
        modalContent.appendChild(modalFooter);

        modal.appendChild(modalContent);
        document.body.appendChild(modal);

        // Обработчик закрытия модального окна
        const closeModal = () => {
            modal.remove();
        };

        // Добавляем обработчики событий для закрытия модального окна
        modal.addEventListener('click', (e) => {
            if (e.target === modal) {
                closeModal();
            }
        });

        const closeBtn = modal.querySelector('.aio-image-modal-close');
        if (closeBtn) {
            closeBtn.addEventListener('click', closeModal);
        }

        // Обработчик клавиши Escape для закрытия модального окна
        const handleEscKey = (e) => {
            if (e.key === 'Escape') {
                closeModal();
                document.removeEventListener('keydown', handleEscKey);
            }
        };

        document.addEventListener('keydown', handleEscKey);
    }

    // Отключаем обработчики карты, когда пользователь взаимодействует с AIO popup
    disableMapHandlers() {
        this.map._handlers.forEach(handler => {
            if (handler.disable && handler._enabled) {
                handler.disable();
            }
        });
    }

    // Включаем обработчики карты, когда пользователь покидает AIO popup
    enableMapHandlers() {
        this.map._handlers.forEach(handler => {
            if (handler.enable && !handler._enabled) {
                handler.enable();
            }
        });
    }

    // Обработчик клика по карте для отображения/скрытия AIO popup
    handleAIOMapClick(e) {
        if (this.aioPopupContainer && this.aioPopupContainer.style.display === 'block') {
            const clickedOnMap = e.originalEvent.target === this.map._container ||
                this.map._container.contains(e.originalEvent.target);

            if (clickedOnMap && !this.aioPopupContainer.contains(e.originalEvent.target)) {
                this.hideAIOPopup();
            }
        }
    }    
   
    // Удаляем AIO с карты и освобождаем ресурсы
    destroy() {
        try {
            // Очищаем выделение
            this.clearHighlight();
            
            // Очищаем карту элементов с принудительным освобождением памяти
            if (this.aioElementsMap) {
                this.aioElementsMap.forEach((elements, id) => {
                    if (Array.isArray(elements)) {
                        elements.forEach(element => {
                            if (element && element.remove) {
                                element.remove();
                            }
                        });
                    }
                });
                this.aioElementsMap.clear();
                this.aioElementsMap = null;
            }
            
            // Очищаем контейнер всплывающих окон AIO
            if (this.aioPopupContainer) {
                if (this.aioPopupContainer.parentNode) {
                    this.aioPopupContainer.parentNode.removeChild(this.aioPopupContainer);
                }
                this.aioPopupContainer = null;
            }
            
            if (this.aioPopupContent) {
                this.aioPopupContent = null;
            }

            // Удаляем слои с принудительной очисткой
            if (this.overlays && this.overlays.AIO) {
                if (this.map && this.map.hasLayer(this.overlays.AIO)) {
                    this.map.removeLayer(this.overlays.AIO);
                }
                
                // Очищаем все слои внутри группы
                if (this.overlays.AIO.clearLayers) {
                    this.overlays.AIO.clearLayers();
                }
                
                this.overlays.AIO = null;
            }
            
            // Очищаем все overlays
            if (this.overlays) {
                Object.keys(this.overlays).forEach(key => {
                    const layer = this.overlays[key];
                    if (layer && this.map && this.map.hasLayer(layer)) {
                        this.map.removeLayer(layer);
                    }
                    if (layer && layer.clearLayers) {
                        layer.clearLayers();
                    }
                });
                this.overlays = {};
            }
            
            // Удаляем обработчик клика по карте
            if (this.map) {
                this.map.off('click', this.handleAIOMapClick.bind(this));
            }
            
            // Удаляем обработчики событий
            if (typeof eventBus !== 'undefined' && eventBus) {
                eventBus.$off('close-aio-popup');
                eventBus.$off('highlight-aio-on-map', this.highlightAIOElements.bind(this));
                eventBus.$off('clear-aio-highlight', this.clearHighlight.bind(this));
            }
            
            // Обнуляем ссылки
            this.currentHighlightedId = null;
            this.map = null;
            this.layerControl = null;
            
        } catch (error) {
            console.warn('Ошибка при очистке AIO layer:', error);
            
            // В случае ошибки все равно обнуляем основные ссылки
            this.aioElementsMap = null;
            this.aioPopupContainer = null;
            this.aioPopupContent = null;
            this.overlays = {};
            this.currentHighlightedId = null;
            this.map = null;
            this.layerControl = null;
        }
    }
}